import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { ArrowsClockwise, CircleNotch, Plus, Trash } from "@phosphor-icons/react";
import { useToast } from "@/hooks/use-toast";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export function SyncSection() {
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [status, setStatus] = useState(null);
  const [times, setTimes] = useState([]);
  const [history, setHistory] = useState([]);
  const [savingTimes, setSavingTimes] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/fm/products/cache-status");
      setStatus(data);
      setSyncing(data.syncing);
      return data;
    } catch {
      return null;
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    // Schedule + history endpoint is admin-only. Skip the call for office
    // users to avoid a spurious 403 in the network tab.
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/fm/sync-settings");
      setTimes(data.times);
      setHistory(data.history);
    } catch {
      /* handled via toast on save */
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchStatus();
    fetchSettings();
  }, [fetchStatus, fetchSettings]);

  useEffect(() => {
    if (!syncing) return;
    const interval = setInterval(async () => {
      const data = await fetchStatus();
      if (data && !data.syncing) {
        toast({ title: "Product list synced with FileMaker" });
        fetchSettings();
      }
    }, 2000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncing]);

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/fm/products/refresh-cache");
      setStatus(data);
      toast({ title: "Sync started - progress shown below" });
    } catch (e) {
      setSyncing(false);
      toast({ title: "Sync failed to start", description: formatApiErrorDetail(e.response?.data?.detail), variant: "destructive" });
    }
  };

  const handleSaveTimes = async () => {
    setSavingTimes(true);
    try {
      const { data } = await api.put("/fm/sync-settings", { times: times.filter(Boolean) });
      setTimes(data.times);
      toast({ title: "Sync schedule saved" });
    } catch (e) {
      toast({ title: "Failed to save schedule", description: formatApiErrorDetail(e.response?.data?.detail), variant: "destructive" });
    }
    setSavingTimes(false);
  };

  const setTimeAt = (i, v) => setTimes((prev) => prev.map((t, idx) => (idx === i ? v : t)));

  return (
    <div className="flex max-w-2xl flex-col gap-6" data-testid="settings-sync-section">
      <div className="rounded-sm border border-zinc-300 p-5">
        <h3 className="font-heading text-sm font-bold text-zinc-950">Sync Status</h3>
        {status?.syncing ? (
          <div className="mt-3 flex flex-col gap-2" data-testid="sync-status-live">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-blue-700" data-testid="sync-status-phase">
                {status.sync_phase || "Starting sync..."}
              </span>
              {status.sync_total > 0 && (
                <span className="font-mono-data text-xs text-zinc-500" data-testid="sync-status-counts">
                  {status.sync_done.toLocaleString()} / {status.sync_total.toLocaleString()}
                  {" "}({Math.round((status.sync_done / Math.max(status.sync_total, 1)) * 100)}%)
                </span>
              )}
            </div>
            {/* Progress bar - width scales with sync_done/sync_total. Falls
                back to an indeterminate-style pulse when total is unknown
                (e.g. during the initial "Fetching product records" phase). */}
            <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-200">
              <div
                className={`h-full bg-blue-600 transition-all duration-500 ease-out ${
                  status.sync_total > 0 ? "" : "animate-pulse"
                }`}
                style={{
                  width:
                    status.sync_total > 0
                      ? `${Math.min(100, (status.sync_done / status.sync_total) * 100)}%`
                      : "35%",
                }}
                data-testid="sync-progress-bar"
              />
            </div>
            <p className="text-xs text-zinc-500">
              This runs in the background - you can leave this page. Photo and pricing sync usually takes a few minutes.
            </p>
          </div>
        ) : (
          <div className="mt-2 flex flex-col gap-1 text-sm text-zinc-600">
            <span data-testid="sync-status-line">
              {status?.last_synced_at
                ? `Last synced: ${new Date(status.last_synced_at).toLocaleString()} — ${status.count.toLocaleString()} products`
                : "Not yet synced"}
            </span>
            {status?.last_sync_error && (
              <span className="text-xs text-red-500" data-testid="sync-status-error">
                Last sync failed: {status.last_sync_error}
              </span>
            )}
          </div>
        )}
        <Button
          size="sm"
          className="mt-4 rounded-sm bg-blue-600 text-white hover:bg-blue-700"
          disabled={syncing}
          onClick={handleSyncNow}
          data-testid="settings-sync-now-button"
        >
          <ArrowsClockwise size={14} className={`mr-1.5 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Syncing..." : "Sync Now"}
        </Button>
      </div>

      {isAdmin && (
      <div className="rounded-sm border border-zinc-300 p-5">
        <h3 className="font-heading text-sm font-bold text-zinc-950">Automatic Sync Schedule</h3>
        <p className="mt-1 text-sm text-zinc-500">
          Daily automatic sync times (Eastern Time / 美东时间, 24h format). 1 to 6 time slots.
        </p>
        <div className="mt-4 flex flex-col gap-2">
          {times.map((t, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                type="time"
                value={t}
                onChange={(e) => setTimeAt(i, e.target.value)}
                className="h-9 w-36 rounded-sm border-zinc-300"
                data-testid={`sync-time-input-${i}`}
              />
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm border-zinc-300"
                disabled={times.length <= 1}
                onClick={() => setTimes((prev) => prev.filter((_, idx) => idx !== i))}
                data-testid={`sync-time-remove-${i}`}
              >
                <Trash size={14} />
              </Button>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm border-zinc-300"
            disabled={times.length >= 6}
            onClick={() => setTimes((prev) => [...prev, "09:00"])}
            data-testid="sync-time-add-button"
          >
            <Plus size={14} className="mr-1.5" /> Add Time
          </Button>
          <Button
            size="sm"
            className="rounded-sm bg-blue-600 text-white hover:bg-blue-700"
            disabled={savingTimes || times.some((t) => !t)}
            onClick={handleSaveTimes}
            data-testid="sync-times-save-button"
          >
            {savingTimes ? <CircleNotch size={14} className="mr-1.5 animate-spin" /> : null}
            Save Schedule
          </Button>
        </div>
      </div>
      )}

      {isAdmin && (
      <div className="rounded-sm border border-zinc-300 p-5">
        <h3 className="font-heading text-sm font-bold text-zinc-950">Sync History</h3>
        {history.length === 0 ? (
          <p className="mt-2 text-sm text-zinc-500">No syncs recorded yet.</p>
        ) : (
          <Table className="mt-3" data-testid="sync-history-table">
            <TableHeader>
              <TableRow className="border-b border-zinc-300">
                <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500">Time</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500">Products</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500">Duration</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((h, i) => (
                <TableRow key={i} className="border-b border-zinc-200">
                  <TableCell className="text-sm">{new Date(h.at).toLocaleString()}</TableCell>
                  <TableCell className="text-sm">
                    {h.status === "success" ? (
                      <span className="text-green-600">Success</span>
                    ) : (
                      <span className="text-red-500" title={h.error}>Failed</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm font-mono-data">{h.count ?? "-"}</TableCell>
                  <TableCell className="text-sm font-mono-data">{h.duration_s != null ? `${h.duration_s}s` : "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      )}
    </div>
  );
}
